/**
 * pin-store — viewer-side generalized pin state.
 *
 * Replaces the legacy account-only `homepage-pins.ts` with a Map keyed by
 * `(scope, surface)` so the same store backs all six pinning surfaces:
 *
 *   account / feed        — homepage feed pins (blocks + collections)
 *   account / tab         — top-nav-bar tab pins (collections only)
 *   collection / default  — pins inside Group/Collection/Stack
 *
 * Each slot persists to:
 *   - the server (Pin table via `/v1/pin/*`)
 *   - localStorage (offline cache + cross-tab sync via `storage` event)
 *
 * Same UX invariants as the old store:
 *   - useSyncExternalStore for React reactivity
 *   - optimistic toggle with rollback on API failure
 *   - MAX_PINS_PER_SURFACE enforced both server-side and client-side
 */
import { useSyncExternalStore } from "react";
import { MAX_PINS_PER_SURFACE, MAX_COLLECTION_PINS_PER_SURFACE, pinItemKey, PinItemRefSchema, } from "@shared/types/pin";
import { fetchPins } from "@/api/query-api";
import { togglePin as togglePinApi, setPins as setPinsApi } from "@/api/command-api";
import { showToast } from "@shared/client/components/toast/show-toast";
function pinCapFor(scope) {
    return scope.type === "collection"
        ? MAX_COLLECTION_PINS_PER_SURFACE
        : MAX_PINS_PER_SURFACE;
}
// ---------------------------------------------------------------------------
// Slot keys & storage
// ---------------------------------------------------------------------------
/** localStorage prefix; the suffix is the slot key. */
const STORAGE_PREFIX = "viewer-pins-v1:";
/**
 * Compose the slot key. `account` scope folds the user's accountId into
 * the suffix because cross-account sessions in the same browser tab need
 * to land in distinct localStorage buckets — but in practice the viewer
 * only ever has one account active at a time, so we use a static suffix.
 */
function slotKey(scope, surface) {
    if (scope.type === "account")
        return `account:${surface}`;
    return `collection:${scope.id}:${surface}`;
}
function storageKey(scope, surface) {
    return `${STORAGE_PREFIX}${slotKey(scope, surface)}`;
}
/** Snapshot per slot. Frozen for referential stability across renders. */
const snapshots = new Map();
const listeners = new Map();
let apiSyncEnabled = false;
function getSlotListeners(key) {
    let set = listeners.get(key);
    if (!set) {
        set = new Set();
        listeners.set(key, set);
    }
    return set;
}
function emit(key) {
    const set = listeners.get(key);
    if (!set)
        return;
    for (const l of set)
        l();
}
function getSlotSnapshot(key) {
    return snapshots.get(key) ?? EMPTY;
}
const EMPTY = Object.freeze([]);
// ---------------------------------------------------------------------------
// localStorage persistence
// ---------------------------------------------------------------------------
function parseStored(raw) {
    if (!raw)
        return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        const out = [];
        const seen = new Set();
        for (const x of parsed) {
            const one = PinItemRefSchema.safeParse(x);
            if (!one.success)
                continue;
            const key = pinItemKey(one.data);
            if (seen.has(key))
                continue;
            seen.add(key);
            out.push(one.data);
            if (out.length >= MAX_COLLECTION_PINS_PER_SURFACE)
                break;
        }
        return out;
    }
    catch (e) {
        console.warn("Failed to parse pins:", e);
        return [];
    }
}
function loadFromStorage(scope, surface) {
    if (typeof window === "undefined")
        return [];
    try {
        return parseStored(localStorage.getItem(storageKey(scope, surface)));
    }
    catch (e) {
        console.warn("Failed to load pins:", e);
        return [];
    }
}
function saveToStorage(scope, surface, items) {
    if (typeof window === "undefined")
        return;
    try {
        localStorage.setItem(storageKey(scope, surface), JSON.stringify(items));
    }
    catch (e) {
        console.warn("Failed to save pins:", e);
    }
}
function clearStorage(scope, surface) {
    if (typeof window === "undefined")
        return;
    try {
        localStorage.removeItem(storageKey(scope, surface));
    }
    catch {
        /* ignore */
    }
}
// Cross-tab sync — only meaningful for account-scope keys since collection
// scope state is naturally per-scope-id anyway.
if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
        if (!e.key || !e.key.startsWith(STORAGE_PREFIX))
            return;
        const key = e.key.slice(STORAGE_PREFIX.length);
        const parsed = parseStored(e.newValue);
        snapshots.set(key, Object.freeze(parsed));
        emit(key);
    });
}
// ---------------------------------------------------------------------------
// Public state API
// ---------------------------------------------------------------------------
export function setPinsApiSyncEnabled(enabled) {
    apiSyncEnabled = enabled;
}
function ensureLoaded(scope, surface) {
    const key = slotKey(scope, surface);
    const existing = snapshots.get(key);
    if (existing)
        return existing;
    const fromStorage = loadFromStorage(scope, surface);
    const frozen = Object.freeze(fromStorage);
    snapshots.set(key, frozen);
    return frozen;
}
export function getPinSnapshot(scope, surface) {
    return ensureLoaded(scope, surface);
}
export function subscribePins(scope, surface, listener) {
    const key = slotKey(scope, surface);
    ensureLoaded(scope, surface);
    const set = getSlotListeners(key);
    set.add(listener);
    return () => {
        set.delete(listener);
    };
}
export function isPinTargetPinned(scope, surface, target) {
    const snap = ensureLoaded(scope, surface);
    return snap.some((p) => p.type === target.type && p.id === target.id);
}
/** Replace the pin list for a slot without calling the API. */
export function replacePins(scope, surface, items) {
    const trimmed = items.slice(0, pinCapFor(scope));
    const frozen = Object.freeze(trimmed);
    const key = slotKey(scope, surface);
    snapshots.set(key, frozen);
    saveToStorage(scope, surface, frozen);
    emit(key);
}
function applyToggleLocal(scope, surface, target) {
    const key = slotKey(scope, surface);
    const current = [...ensureLoaded(scope, surface)];
    const idx = current.findIndex((p) => p.type === target.type && p.id === target.id);
    if (idx >= 0) {
        current.splice(idx, 1);
        const next = Object.freeze(current);
        snapshots.set(key, next);
        saveToStorage(scope, surface, next);
        emit(key);
        return { ok: true, pinned: false };
    }
    if (current.length >= pinCapFor(scope)) {
        return { ok: false, reason: "max" };
    }
    const next = Object.freeze([target, ...current]);
    snapshots.set(key, next);
    saveToStorage(scope, surface, next);
    emit(key);
    return { ok: true, pinned: true };
}
/**
 * Toggle a pin with API sync. Rolls back local state on failure.
 *
 * Uses the dedicated `POST /v1/pin/toggle` endpoint instead of a full
 * `setAll` write, so a 409 from the server (slot full) doesn't require
 * re-shipping the whole list.
 */
export async function togglePinWithSync(scope, surface, target) {
    const key = slotKey(scope, surface);
    const previous = [...ensureLoaded(scope, surface)];
    const local = applyToggleLocal(scope, surface, target);
    if (!local.ok)
        return local;
    if (!apiSyncEnabled)
        return local;
    try {
        const result = await togglePinApi(scope, surface, target);
        // Server is the source of truth for ordering. Reconcile from the
        // server's response so a position shift we didn't anticipate locally
        // settles correctly in the cache.
        const fromServer = result.pins.map((p) => ({
            type: p.itemType,
            id: p.itemId,
        }));
        const frozen = Object.freeze(fromServer);
        snapshots.set(key, frozen);
        saveToStorage(scope, surface, frozen);
        emit(key);
        return { ok: true, pinned: result.pinned };
    }
    catch (e) {
        const rollback = Object.freeze(previous);
        snapshots.set(key, rollback);
        saveToStorage(scope, surface, rollback);
        emit(key);
        // Server rejected with 409 (slot full) — local state was stale (e.g. another
        // tab added a pin since the last bootstrap). Surface the max-pins warning so
        // the caller shows the same UX as the client-side pre-check path.
        if (e instanceof Error && e.message.startsWith("HTTP 409:")) {
            return { ok: false, reason: "max" };
        }
        console.warn("Failed to sync pin toggle:", e);
        showToast.error("Could not save pin. Check your connection and try again.");
        return { ok: false, reason: "sync" };
    }
}
/**
 * Remove a single item from a slot's pin list if present. Used on
 * block-delete cleanup, where we don't want the API failure to restore
 * a now-deleted block's pin.
 */
export async function removePinIfPresent(scope, surface, target) {
    const key = slotKey(scope, surface);
    const current = [...ensureLoaded(scope, surface)];
    const idx = current.findIndex((p) => p.type === target.type && p.id === target.id);
    if (idx < 0)
        return;
    current.splice(idx, 1);
    const next = Object.freeze(current);
    snapshots.set(key, next);
    saveToStorage(scope, surface, next);
    emit(key);
    if (!apiSyncEnabled)
        return;
    try {
        await setPinsApi(scope, surface, [...next]);
    }
    catch (e) {
        console.warn("Failed to sync pin removal:", e);
        showToast.error("Could not update pins on the server. Refresh to sync.");
    }
}
/**
 * Reorder a pin from one position to another within a (scope, surface)
 * slot and sync to the API. Rolls back local state on failure (same
 * pattern as `togglePinWithSync`).
 *
 * Indices reference the current `getPinSnapshot(scope, surface)`
 * snapshot. No-ops when the indices are equal or out of range.
 */
export async function reorderPinsWithSync(scope, surface, fromIndex, toIndex) {
    if (fromIndex === toIndex)
        return;
    const key = slotKey(scope, surface);
    const current = ensureLoaded(scope, surface);
    if (fromIndex < 0 || fromIndex >= current.length)
        return;
    if (toIndex < 0 || toIndex >= current.length)
        return;
    const previous = [...current];
    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    if (!moved)
        return;
    next.splice(toIndex, 0, moved);
    const frozen = Object.freeze(next);
    snapshots.set(key, frozen);
    saveToStorage(scope, surface, frozen);
    emit(key);
    if (!apiSyncEnabled)
        return;
    try {
        await setPinsApi(scope, surface, [...frozen]);
    }
    catch (e) {
        console.warn("Failed to sync pin reorder:", e);
        const rollback = Object.freeze(previous);
        snapshots.set(key, rollback);
        saveToStorage(scope, surface, rollback);
        emit(key);
        showToast.error("Could not save pin order. Check your connection and try again.");
    }
}
/**
 * Bootstrap a slot from the API. Pulls authoritative state from the
 * server when authenticated; falls back to localStorage if the network
 * fails. If localStorage holds pins but the server has none, the local
 * pins are uploaded to seed the server (one-shot migration after a
 * fresh login).
 */
export async function bootstrapPinsFromApi(scope, surface) {
    if (!apiSyncEnabled) {
        const fromStorage = loadFromStorage(scope, surface);
        replacePins(scope, surface, fromStorage);
        return;
    }
    let remoteWasEmpty = false;
    try {
        const remote = await fetchPins(scope, surface);
        const items = remote.map((p) => ({ type: p.itemType, id: p.itemId }));
        if (items.length > 0) {
            replacePins(scope, surface, items);
            return;
        }
        remoteWasEmpty = true;
    }
    catch (e) {
        console.warn("Failed to fetch pins during bootstrap:", e);
    }
    const local = loadFromStorage(scope, surface);
    if (local.length > 0) {
        replacePins(scope, surface, local);
        if (remoteWasEmpty) {
            try {
                await setPinsApi(scope, surface, local);
            }
            catch (e) {
                console.warn("Failed to upload migrated pins:", e);
            }
        }
        return;
    }
    replacePins(scope, surface, []);
}
/**
 * Clear in-memory and local state for every slot. Called on sign-out so
 * the next account's session doesn't see the previous user's pins.
 */
export function resetPinsLocalState() {
    // Capture keys before wiping so we can fire emit() on each cleared slot.
    const keys = [...snapshots.keys()];
    snapshots.clear();
    if (typeof window !== "undefined") {
        try {
            const toDelete = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith(STORAGE_PREFIX))
                    toDelete.push(k);
            }
            for (const k of toDelete)
                localStorage.removeItem(k);
        }
        catch {
            /* ignore */
        }
    }
    for (const key of keys)
        emit(key);
}
/** Public mirror of `clearStorage` for one specific slot. */
export function clearSlotStorage(scope, surface) {
    clearStorage(scope, surface);
}
// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------
/**
 * Reactive pin list for a (scope, surface) slot. Re-renders when the
 * slot changes — including cross-tab sync via the `storage` event.
 */
export function usePinList(scope, surface) {
    const key = slotKey(scope, surface);
    return useSyncExternalStore((l) => {
        ensureLoaded(scope, surface);
        const set = getSlotListeners(key);
        set.add(l);
        return () => {
            set.delete(l);
        };
    }, () => getSlotSnapshot(key), () => EMPTY);
}
